import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FsLeaderboardUser } from "db/model/leaderboard/FsLeaderboardUser";
import {
  collection,
  doc,
  DocumentData,
  DocumentSnapshot,
  getDoc,
  getDocs,
  limit,
  query,
  QuerySnapshot,
  where,
} from "firebase/firestore";
import { leaderboardCollectionName } from "db/info/collectionNames";
import { db } from "firebase-config";
import { Leaderboard } from "model/Leaderboard/Leaderboard";
import { LeaderboardUser } from "model/Leaderboard/User/LeaderboardUser";
import { mapLeaderboardUser } from "db/mapping/leaderboard/FsLeaderboardUser";

export interface ILeaderboardSlice {
  activeFetches: { [key: string]: number };
  fetchingLeaderboardUsers: FetchStatus;
  intialLoadComplete: boolean;
  leaderboards: { [key: string]: Leaderboard };
}

type FetchStatus = "idle" | "error" | "fetching"; // use this kind of ting instead of enums and unbounded strings innit, reusuable in other slices???

const initialState: ILeaderboardSlice = {
  activeFetches: {},
  fetchingLeaderboardUsers: "idle",
  intialLoadComplete: false,
  leaderboards: {},
};

export type FetchLeaderboardUsersByUidPayload = {
  leaderboardId: string;
  uids: string[];
};

export type FetchLeaderboardUsersBySortPayload = {
  leaderboardId: string;
  sortsToFetch: number[];
};

export const fetchLeaderboardUsersByUid = createAsyncThunk(
  "users/fetchLeaderboardUsersByUid",
  async (payload: FetchLeaderboardUsersByUidPayload) => {
    const { leaderboardId, uids } = payload;
    const promises: Promise<DocumentSnapshot<DocumentData>>[] = [];
    const leaderboardUsers = new Map<string, LeaderboardUser | undefined>();

    uids.forEach((uid) => {
      const d = doc(db, leaderboardCollectionName, leaderboardId, "user", uid);
      promises.push(getDoc(d));
    });

    const snapshots = await Promise.all(promises);
    snapshots.forEach((snapshot) => {
      if (snapshot.exists()) {
        leaderboardUsers.set(
          snapshot.id,
          mapLeaderboardUser(snapshot.data() as FsLeaderboardUser, snapshot.id)
        );
      }
    });

    return { leaderboardUsers, leaderboardId, uids };
  }
);

export const fetchLeaderboardUsersBySort = createAsyncThunk(
  "users/fetchLeaderboardUsersBySort",
  async (payload: FetchLeaderboardUsersBySortPayload) => {
    const { leaderboardId, sortsToFetch } = payload;
    const promises: Promise<QuerySnapshot<DocumentData>>[] = [];
    const leaderboardUsers = new Map<string, LeaderboardUser | undefined>();

    for (var i = 0; i < sortsToFetch.length; i++) {
      const q = query(
        collection(db, leaderboardCollectionName, leaderboardId, "user"),
        where("sort", "==", sortsToFetch[i]),
        limit(1)
      );
      promises.push(getDocs(q));
    }

    const snapshots = await Promise.all(promises);
    snapshots.forEach((snapshot) => {
      const doc = snapshot.docs.length > 0 ? snapshot.docs[0] : null;
      if (doc !== null) {
        leaderboardUsers.set(
          doc.id,
          mapLeaderboardUser(doc?.data() as FsLeaderboardUser, doc.id)
        );
      }
    });

    return { leaderboardUsers, leaderboardId, sortsToFetch };
  }
);

// todo - should the FSModel -> Model mapping be done in the slice ??
export const leaderboardSlice = createSlice({
  name: "trackmania",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    updateLeaderboards: (
      state,
      action: PayloadAction<{
        leaderboards: Leaderboard[];
      }>
    ) => {
      const { leaderboards } = action.payload;
      for (let i = 0; i < leaderboards.length; i++) {
        const leaderboard = leaderboards[i];
        state.leaderboards[leaderboard.id] = leaderboard;
      }

      if (!state.intialLoadComplete) {
        state.intialLoadComplete = true;
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchLeaderboardUsersBySort.pending, (state, action) => {
        state.fetchingLeaderboardUsers = "fetching";
        const lastUpdated =
          state.leaderboards[action.meta.arg.leaderboardId].lastUpdated;
        state.activeFetches[action.meta.requestId] = lastUpdated;
      })
      .addCase(fetchLeaderboardUsersBySort.fulfilled, (state, action) => {
        const { leaderboardUsers, leaderboardId, sortsToFetch } =
          action.payload;
        const leaderboard = state.leaderboards[leaderboardId];

        if (
          state.activeFetches[action.meta.requestId] !== leaderboard.lastUpdated
        ) {
          console.log("Leaderboard updated, request cancelled");
          state.fetchingLeaderboardUsers = "idle";
          delete state.activeFetches[action.meta.requestId];
          return;
        }

        Array.from(leaderboardUsers.keys()).forEach((uid) => {
          leaderboard.users[uid] = leaderboardUsers.get(uid)!;
        });

        const missingSorts = sortsToFetch.filter(
          (s) =>
            !Array.from(leaderboardUsers.values()).find((u) => u?.sort === s)
        );
        missingSorts.forEach((s) => leaderboard.missingSorts.push(s));

        state.fetchingLeaderboardUsers = "idle";
        delete state.activeFetches[action.meta.requestId];
      })
      // todo - rejected case
      .addCase(fetchLeaderboardUsersBySort.rejected, (state, action) => {
        console.log(action.error.message);
        delete state.activeFetches[action.meta.requestId];
        state.fetchingLeaderboardUsers = "error";
      })
      .addCase(fetchLeaderboardUsersByUid.pending, (state, action) => {
        const lastUpdated =
          state.leaderboards[action.meta.arg.leaderboardId].lastUpdated;
        state.activeFetches[action.meta.requestId] = lastUpdated;
        state.fetchingLeaderboardUsers = "fetching";
      })
      .addCase(fetchLeaderboardUsersByUid.fulfilled, (state, action) => {
        const { leaderboardUsers, leaderboardId, uids } = action.payload;
        const leaderboard = state.leaderboards[leaderboardId];

        if (
          state.activeFetches[action.meta.requestId] !== leaderboard.lastUpdated
        ) {
          console.log("Leaderboard updated, request cancelled");
          state.fetchingLeaderboardUsers = "idle";
          delete state.activeFetches[action.meta.requestId];
          return;
        }

        const lbUserKeys = Array.from(leaderboardUsers.keys());
        uids.forEach((uid) => {
          if (lbUserKeys.includes(uid)) {
            leaderboard.users[uid] = leaderboardUsers.get(uid)!;
          } else {
            leaderboard.users[uid] = undefined;
          }
        });
        state.fetchingLeaderboardUsers = "idle";
        delete state.activeFetches[action.meta.requestId];
      })
      // todo - rejected case
      .addCase(fetchLeaderboardUsersByUid.rejected, (state, action) => {
        console.log(action.error.message);
        delete state.activeFetches[action.meta.requestId];
        state.fetchingLeaderboardUsers = "error";
      });
  },
});

export const { updateLeaderboards } = leaderboardSlice.actions;

export const leaderboardReducer = leaderboardSlice.reducer;
